import React, { useCallback } from "react";
import { SyntheticAttachment } from "@components/documents/AccrualDocuments/AccrualDocumentsCompact";
import { useAccrualDocumentLinks } from "@components/documents/AccrualDocuments/useAccrualDocumentLinks";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import {
    AttachmentChip,
    FinancialDocumentAttachment,
} from "@components/documents/FinancialDocumentAttachment/FinancialDocumentAttachment";
import { Button } from "@design-system/Button/Button";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { UploadIcon } from "@icons";
import {
    useLinkDocumentsToInvoice,
    useUnlinkDocumentsFromInvoice,
} from "@api/invoice.api";
import { useScope } from "@state/scopeContext.lib";
import styles from "./InvoiceDocumentsCompact.module.scss";

interface InvoiceDocumentsCompactProps {
    invoiceId: string;
    financialDocumentIds?: number[];
    // Read-only mode (e.g. Void invoices): existing documents and the generated
    // PDF stay viewable, but no link/remove callbacks are supplied.
    isReadOnly?: boolean;
    pdfAttachment?: SyntheticAttachment;
}

export const InvoiceDocumentsCompact: React.FC<
    InvoiceDocumentsCompactProps
> = ({
    invoiceId,
    financialDocumentIds,
    isReadOnly = false,
    pdfAttachment,
}) => {
    const { workspaceId } = useScope();
    const linkMutation = useLinkDocumentsToInvoice();
    const unlinkMutation = useUnlinkDocumentsFromInvoice();

    const onLinkDocuments = useCallback(
        (documentIds: number[], options: { onError: () => void }) => {
            if (!workspaceId) {
                options.onError();
                return;
            }
            linkMutation.mutate(
                {
                    workspaceId,
                    invoiceId,
                    financialDocumentIds: documentIds,
                },
                { onError: options.onError },
            );
        },
        [workspaceId, invoiceId, linkMutation],
    );

    const onUnlinkDocument = useCallback(
        (documentId: number, options: { onError: () => void }) => {
            if (!workspaceId) {
                options.onError();
                return;
            }
            unlinkMutation.mutate(
                {
                    workspaceId,
                    invoiceId,
                    financialDocumentIds: [documentId],
                },
                { onError: options.onError },
            );
        },
        [workspaceId, invoiceId, unlinkMutation],
    );

    const {
        documentIds,
        attachedDocuments,
        isResolvingDocuments,
        hasDocumentsLoadError,
        attachDocument,
        attachDocuments,
        handleDocumentsUpload,
        detachDocument,
    } = useAccrualDocumentLinks({
        financialDocumentIds,
        onLinkDocuments: isReadOnly ? undefined : onLinkDocuments,
        onUnlinkDocument: isReadOnly ? undefined : onUnlinkDocument,
    });

    const hasVisibleAttachments =
        !!pdfAttachment || attachedDocuments.length > 0;
    const showEmptyValue =
        isReadOnly &&
        !hasVisibleAttachments &&
        !isResolvingDocuments &&
        !hasDocumentsLoadError;

    const attachments = showEmptyValue ? (
        <p className={styles.emptyValue}>-</p>
    ) : (
        <div className={styles.list}>
            {!isReadOnly && (
                <FinancialDocumentUploadProvider
                    onCompleted={handleDocumentsUpload}
                >
                    <AddDocument
                        trigger={
                            <Button
                                variant="link"
                                size="sm"
                                className={styles.addButton}
                                leftIcon={<UploadIcon />}
                            >
                                Upload
                            </Button>
                        }
                        attachDocument={attachDocument}
                        attachDocuments={attachDocuments}
                        attachedDocumentIds={documentIds}
                    />
                </FinancialDocumentUploadProvider>
            )}
            {pdfAttachment && (
                <AttachmentChip
                    fileName={pdfAttachment.fileName}
                    onOpen={pdfAttachment.onOpen}
                />
            )}
            {attachedDocuments.map((document) => (
                <FinancialDocumentAttachment
                    key={document.id}
                    financialDocument={document}
                    detachDocument={detachDocument}
                    readonly={isReadOnly}
                />
            ))}
            {hasDocumentsLoadError && (
                <p className={styles.errorValue}>
                    Couldn&apos;t load attachments
                </p>
            )}
        </div>
    );

    return (
        <GridRow gap="0">
            <GridCol sm={4} className={styles.label}>
                Attachments
            </GridCol>
            <GridCol sm={8} className={styles.value}>
                {attachments}
            </GridCol>
        </GridRow>
    );
};
